import React, { useState, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import ApiExplorer from '../components/ApiExplorer';
import DateRangePicker from '../components/DateRangePicker';
import { usePartners } from '../context/PartnersContext';
import { useApiMode } from '../context/ApiModeContext';
import './PageStyles.css';
import './Customers.css';


const getPartnerInitials = (partnerName) => {
  const words = (partnerName || '').trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }
  return (partnerName || '').slice(0, 2).toUpperCase() || 'XX';
};

// Build initial customers with C + partner initials + 6 digit id, one per partner sequence
const buildInitialCustomers = (partners) => {
  const list = [];
  let seqByPartner = {};
  const raw = [
    { name: 'John Doe', email: 'john@example.com', phone: '+1-234-567-8900', status: 'Active', kycStatus: 'Verified', createdAt: '2026-02-01', connectionLabel: 'Connection 1' },
    { name: 'Jane Smith', email: 'jane@example.com', phone: '+1-234-567-8901', status: 'Active', kycStatus: 'Verified', createdAt: '2026-02-05', connectionLabel: 'Connection 1' },
    { name: 'Bob Johnson', email: 'bob@example.com', phone: '+1-234-567-8902', status: 'Pending', kycStatus: 'Pending', createdAt: '2026-02-08', connectionLabel: 'Connection 2' },
    { name: 'Alice Brown', email: 'alice@example.com', phone: '+1-234-567-8903', status: 'Active', kycStatus: 'Verified', createdAt: '2026-02-10', connectionLabel: 'Connection 3' },
  ];
  raw.forEach((r) => {
    const partner = partners.find((p) => p.name === r.connectionLabel) || partners[0];
    const partnerName = partner ? partner.name : r.connectionLabel;
    const initials = getPartnerInitials(partnerName);
    seqByPartner[partnerName] = (seqByPartner[partnerName] || 0) + 1;
    const seq = String(seqByPartner[partnerName]).padStart(6, '0');
    const id = `C${initials}${seq}`;
    list.push({
      id,
      name: r.name,
      email: r.email,
      phone: r.phone,
      status: r.status,
      kycStatus: r.kycStatus,
      createdAt: r.createdAt,
      partnerId: partner ? partner.id : null,
      partnerName,
    });
  });
  return list.sort((a, b) => b.id.localeCompare(a.id));
};

const Customers = () => {
  const { partners, addLogForPartner } = usePartners();
  const { apiBaseUrl } = useApiMode();
  const [customers] = useState(() => buildInitialCustomers(partners));
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterPartnerId, setFilterPartnerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTryApiSidebar, setShowTryApiSidebar] = useState(false);

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      if (filterCustomerId && !c.id.toLowerCase().includes(filterCustomerId.trim().toLowerCase())) return false;
      if (filterName && !c.name.toLowerCase().includes(filterName.trim().toLowerCase())) return false;
      if (filterPartnerId && String(c.partnerId) !== String(filterPartnerId)) return false;
      if (dateFrom && c.createdAt < dateFrom) return false;
      if (dateTo && c.createdAt > dateTo) return false;
      return true;
    });
  }, [customers, filterCustomerId, filterName, filterPartnerId, dateFrom, dateTo]);

  const handleDateChange = (start, end) => {
    setDateFrom(start || '');
    setDateTo(end || '');
  };

  const dateRangeLabel = dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : dateFrom ? `${dateFrom} → Select end` : 'Date range';

  return (
    <DashboardLayout>
      <div className="page-container">
        <div className="card">
          <div className="card-header card-header-customers">
            <h2>Customers</h2>
            <div className="customers-header-actions">
              <button type="button" className="btn btn-try-api" onClick={() => setShowTryApiSidebar(true)}>
                Try API
              </button>
            </div>
          </div>

          <div className="customers-filters">
            <div className="filter-group">
              <label>Customer ID</label>
              <input
                type="text"
                className="form-input filter-input"
                placeholder="Filter by ID"
                value={filterCustomerId}
                onChange={(e) => setFilterCustomerId(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Name</label>
              <input
                type="text"
                className="form-input filter-input"
                placeholder="Filter by name"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Connection</label>
              <select
                className="form-input filter-input"
                value={filterPartnerId}
                onChange={(e) => setFilterPartnerId(e.target.value)}
              >
                <option value="">All connections</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Date range</label>
              <button
                type="button"
                className="form-input filter-input date-range-trigger"
                onClick={() => setShowDatePicker(true)}
              >
                {dateRangeLabel}
              </button>
            </div>
          </div>

          {showDatePicker && (
            <DateRangePicker
              startDate={dateFrom}
              endDate={dateTo}
              onDateChange={handleDateChange}
              onClose={() => setShowDatePicker(false)}
            />
          )}

          <table className="table">
            <thead>
              <tr>
                <th>Customer ID</th>
                <th>Name</th>
                <th>Connection</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td><code className="customer-id-cell">{customer.id}</code></td>
                  <td>{customer.name}</td>
                  <td>{customer.partnerName}</td>
                  <td>{customer.createdAt}</td>
                  <td>
                    <button type="button" className="btn btn-success btn-sm">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showTryApiSidebar && (
          <div className="try-api-sidebar-overlay" onClick={() => setShowTryApiSidebar(false)} aria-hidden="true" />
        )}
        {showTryApiSidebar && (
          <aside className="try-api-sidebar">
            <ApiExplorer
              baseUrl={apiBaseUrl}
              partners={partners}
              onClose={() => setShowTryApiSidebar(false)}
              onLogEntry={addLogForPartner}
            />
          </aside>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Customers;
